'use client';

import { useState, useEffect } from 'react';
import { useI18n } from '@/hooks/use-i18n';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Globe } from 'lucide-react';
import type { SupportedLanguage } from '@/i18n';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { updateUserConfigFromTargetSpace } from '@/services/user';

export function LanguageSelector() {
  const { t, currentLanguage, changeLanguage, supportedLanguages } =
    useI18n('auth');
  const [selectedLanguage, setSelectedLanguage] =
    useState<SupportedLanguage>(currentLanguage);
  const [isThirdParty, setIsThirdParty] = useState(false);

  // 检查是否为第三方接入
  useEffect(() => {
    setIsThirdParty(typeof window !== 'undefined' && window.DM_IS_THIRD_PARTY);
  }, []);

  // 如果是第三方接入，隐藏语言选择器
  if (isThirdParty) {
    return null;
  }

  const handleLanguageChange = async (value: string) => {
    const newLanguage = value as SupportedLanguage;
    setSelectedLanguage(newLanguage);

    if (newLanguage === currentLanguage) {
      return;
    }

    try {
      // 更新本地语言设置
      const success = await changeLanguage(newLanguage);
      if (success) {
        // 更新用户的语言配置到服务器
        try {
          await updateUserConfigFromTargetSpace({
            key: 'language',
            value: newLanguage,
          });
        } catch (error) {
          console.error('更新用户语言设置失败:', error);
        }
        // 刷新当前页面
        window.location.reload();
      } else {
        // toast.error(t('settings.language.changeError'));
      }
    } catch (error) {
      console.error('语言设置失败:', error);
      // toast.error(t('settings.language.changeError'));
    }
  };

  return (
    <SidebarMenuButton asChild>
      <div className="flex items-center justify-between p-2 h-10">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-gray-500" />
          <span className="text-gray-75 text-[13px]">
            {t('settings.language.title')}
          </span>
        </div>
        <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
          <SelectTrigger
            size="none"
            className="h-6 w-fit px-1.5 py-0.5 text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {Object.entries(supportedLanguages).map(([code, info]) => (
              <SelectItem key={code} value={code} className="py-1 text-xs">
                <div className="flex items-center justify-between w-full">
                  <span>{info.nativeName}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </SidebarMenuButton>
  );
}
